import { useState, useEffect, useCallback, useContext } from 'react';
import { Recorder, SubUser } from 'type/type';
import { useNavigate, useParams } from 'react-router-dom';
import hubLayout from 'styles/css/hubLayout.module.css';
import { getRecorderList, getSubUserList, updateSubUserPush } from 'api/api';
import Loading from 'components/common/Loading';
import { useModal } from 'components/common/ModalLayout';
import { GrRefresh } from 'react-icons/gr';
import { styled } from 'styled-components';
import { IoAdd } from 'react-icons/io5';
import { MdKeyboardArrowRight } from "react-icons/md";
import { UserLoginInfoContext } from 'pages/Layout';

const SubUserRow = styled.div`
  dt {
    width: 140px !important;
    font-weight: bold;
  }
`;

const ToggleWrapper = styled.div<{ $isOn: boolean }>`
  width: 90px;
  height: 30px;
  background-color: ${({ $isOn }) => ($isOn ? '#1976d2' : '#e0e0e0')};
  border-radius: 20px;
  border: 2px solid ${({ $isOn }) => ($isOn ? '#1976d2' : '#e0e0e0')};
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 0 10px;
  position: relative;
  font-weight: bold;
  font-size: 14px;
  color: ${({ $isOn }) => ($isOn ? 'white' : '#999')};
  transition: background-color 0.3s;

  .label {
    z-index: 2;
  }

  .switch-handle {
    width: ${({ $isOn }) => ($isOn ? '38px' : '42px')};
    height: 100%;
    background-color: white;
    border-radius: 18px;
    position: absolute;
    top: 0;
    left: 0;
    transform: ${({ $isOn }) => ($isOn ? 'translateX(48px)' : 'translateX(0)')};
    transition: transform 0.3s;
  }
`;

function SubList() {
  const navigate = useNavigate();
  const { showModal } = useModal();
  const { userLoginInfo } = useContext(UserLoginInfoContext);
  const { userId } = useParams();
  const [subUserList, setSubUserList] = useState<SubUser[]>([]);
  const [recorderList, setRecorderList] = useState<Recorder[]>([]);
  const [totalCount, setTotalCount] = useState<number>(0);
  const [loading, setLoading] = useState(true);
  const [isOn, setIsOn] = useState(true);

  const toggle = async (subUserId: string) => {
    const newPushValue = isOn ? 0 : 1;
  
    const targetSubUser = subUserList.find(
      (subUser) => subUser.id === subUserId && subUser.recorder_list?.length
    );
  
    if (!targetSubUser) return showModal("alert", '녹화기를 먼저 등록 후 사용 가능합니다.');;
  
    const updateRs = await updateSubUserPush({ id: subUserId, push: { all: newPushValue } });
    if (updateRs.code !== 200) return;
  
    setSubUserList((prevList) =>
      prevList.map((subUser) =>
      subUser.id === subUserId && subUser.recorder_list?.length
          ? {
              ...subUser,
              recorder_list: subUser.recorder_list.map((item) => ({
                ...item,
                push: { all: newPushValue },
              })),
            }
          : subUser
      )
    );
  
    setIsOn(!isOn);
  };

  const getSubUsers = useCallback(async () => {
    setLoading(true);
    try {
      const subUserRes = await getSubUserList(0, 0, { user_id: userLoginInfo ? userLoginInfo.id : 'x' });
      if (subUserRes.code === 200) {
        setSubUserList(subUserRes.data);
        setTotalCount(subUserRes.total_count);
        
        const recorderRes = await getRecorderList(0, 0, { user_id: userLoginInfo ? userLoginInfo.id : 'x' });
        if (recorderRes.code === 200) {
          setRecorderList(recorderRes.data);
        } else {
          showModal("alert", recorderRes.message);
        }
      } else {
        showModal("alert", subUserRes.message);
      }
    } catch (error) {
      console.log(error);
    } finally {
      setLoading(false);
    }
  }, [userId]);

  const createSubUser = async (url: string) => {
    if (recorderList.length === 0) {
      return showModal('alert', `녹화기를 먼저 등록 후 사용 가능합니다.`);
    }
    if (totalCount > 3) {
      return showModal('alert', `부계정은 최대 4개까지 생성 가능합니다.`);
    }
    navigate(url);
  }

  useEffect(() => {
    getSubUsers();
  }, [getSubUsers]);
  return (
    <>
      {loading ? <Loading /> : 
        <>
          <div className={`${hubLayout.cnt_box}`} style={{ overflowY: "auto" }}>
            <div className={`${hubLayout.acc__cnt}`}>
              <p className={`${hubLayout.acc_total} ${hubLayout.display_flex}`} style={{ margin: '20px 0', justifyContent: "space-between" }}>
                <span>총 <strong className={`${hubLayout.tx_blue}`}>{totalCount}</strong>개</span>
                <span onClick={getSubUsers} style={{ paddingRight: '10px' }}><GrRefresh /></span>
              </p>
              <SubUserRow>
                <ul className={`${hubLayout.acc_list}`}>
                  {totalCount === 0 ?
                    <li className={`${hubLayout.acc_item}`}>
                      <div className={`${hubLayout.acc_item_info}`}>
                        <dl>
                          <dd style={{textAlign: "center"}}>등록된 부계정이 없습니다.</dd>
                        </dl>
                      </div>
                    </li>
                  :
                    subUserList.map((subUser, index) => {
                      const isPushOn = subUser.recorder_list?.[0]?.push?.all === 1;
                    
                      return (
                        <li key={`sub_${index}`} className={hubLayout.acc_item}>
                          <div
                            className={hubLayout.acc_item_top}
                            onClick={() => navigate(`/user/sub/process/${subUser.idx}`)}
                          >
                            <p className={hubLayout.name}>{subUser.id}</p>
                            <p className={hubLayout.id}>{subUser.name}</p>
                            <span style={{ fontSize: '20px' }}>
                              <MdKeyboardArrowRight />
                            </span>
                          </div>
                    
                          <div className={hubLayout.acc_item_info}>
                            <dl>
                              <dt>이벤트 알림 전송</dt>
                              <dd>
                                <ToggleWrapper $isOn={isPushOn} onClick={() => toggle(subUser.id)}>
                                  <span className="label">{isPushOn ? 'ON' : 'OFF'}</span>
                                  <div className="switch-handle" />
                                </ToggleWrapper>
                              </dd>
                            </dl>
                          </div>
                        </li>
                      );
                    })                  
                  }
                </ul>
              </SubUserRow>
              <button type="button" className={`${hubLayout.floating_button}`} onClick={() => createSubUser('/user/sub/process')}>
                <IoAdd />
              </button>
            </div>
          </div>
        </>
      }
    </>
  );
}

export default SubList;